import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMainFreeMedicalCategoryDelete,
  useMainFreeMedicalCategoryCreate,
  useMainFreeMedicalCategoryUpdate,
  useMainFreeMedicalTreatmentTypeDelete,
  useMainFreeMedicalTreatmentTypeCreate,
  useMainFreeMedicalTreatmentTypeUpdate,
  getCategoryById,
  getTreatmentTypeById,
} from './useMainFreeMedical';
import { useClinicContext } from './useClinicContext';
import {
  FreeMedicalTreatmentCategory,
  FreeMedicalTreatmentType,
  CategoryFormData,
  TreatmentTypeFormData,
} from '@/pages/main/freeMedical/types';
import useAppToast from '../useAppToast';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';

/**
 * Custom hook to manage modal states and handlers for Free Medical page
 */
export const useFreeMedicalCategoryModal = () => {
  const toast = useAppToast();
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  const [categoryEditModalOpen, setCategoryEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<FreeMedicalTreatmentCategory | null>(
    null
  );
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const createCategoryMutation = useMainFreeMedicalCategoryCreate();
  const updateCategoryMutation = useMainFreeMedicalCategoryUpdate();
  const deleteCategoryMutation = useMainFreeMedicalCategoryDelete();

  const handleNewCategory = useCallback(() => {
    setSelectedCategory(null);
    setIsNewCategory(true);
    setCategoryEditModalOpen(true);
  }, []);

  const handleEditCategory = useCallback(
    async (category: FreeMedicalTreatmentCategory) => {
      try {
        setIsLoadingDetail(true);
        // Fetch the full category details from the backend
        const response = await getCategoryById(tenant, clinicNumber, category.id);
        if (response.success && response.data) {
          setSelectedCategory(response.data);
          setIsNewCategory(false);
          setCategoryEditModalOpen(true);
        } else {
          toast.error(response.message || 'カテゴリの取得に失敗しました。');
        }
      } catch (err: any) {
        toast.error(
          isNotFoundError(err)
            ? 'データが見つかりません。'
            : getApiErrorMessage(err, 'カテゴリの取得に失敗しました。')
        );
        if (isNotFoundError(err)) {
          queryClient.invalidateQueries({ queryKey: ['free-medical', 'categories'] });
        }
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [tenant, clinicNumber, toast, queryClient]
  );

  const handleSaveCategory = useCallback(
    async (data: CategoryFormData) => {
      try {
        if (isNewCategory) {
          await createCategoryMutation.mutateAsync(data);
        } else if (selectedCategory) {
          await updateCategoryMutation.mutateAsync({ id: selectedCategory.id, data });
        }
        setCategoryEditModalOpen(false);
        setSelectedCategory(null);
        setIsNewCategory(false);
      } catch (err: any) {
        throw { errors: err?.errors || {} };
      }
    },
    [isNewCategory, selectedCategory, createCategoryMutation, updateCategoryMutation]
  );

  return {
    categoryEditModalOpen,
    setCategoryEditModalOpen,
    selectedCategory,
    isNewCategory,
    isLoadingDetail,
    handleNewCategory,
    handleEditCategory,
    deleteCategoryMutation,
    handleSaveCategory,
  };
};

/**
 * Custom hook to manage modal states and handlers for Treatment Type
 */
export const useFreeMedicalTreatmentTypeModal = () => {
  const toast = useAppToast();
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  const [treatmentTypeEditModalOpen, setTreatmentTypeEditModalOpen] = useState(false);
  const [selectedTreatmentType, setSelectedTreatmentType] =
    useState<FreeMedicalTreatmentType | null>(null);
  const [isNewTreatmentType, setIsNewTreatmentType] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const createTreatmentTypeMutation = useMainFreeMedicalTreatmentTypeCreate();
  const updateTreatmentTypeMutation = useMainFreeMedicalTreatmentTypeUpdate();
  const deleteTreatmentTypeMutation = useMainFreeMedicalTreatmentTypeDelete();

  const handleNewTreatmentType = useCallback(() => {
    setSelectedTreatmentType(null);
    setIsNewTreatmentType(true);
    setTreatmentTypeEditModalOpen(true);
  }, []);

  const handleEditTreatmentType = useCallback(
    async (treatmentType: FreeMedicalTreatmentType) => {
      try {
        setIsLoadingDetail(true);
        // Fetch the full treatment type details from the backend
        const response = await getTreatmentTypeById(tenant, clinicNumber, treatmentType.id);
        if (response.success && response.data) {
          setSelectedTreatmentType(response.data);
          setIsNewTreatmentType(false);
          setTreatmentTypeEditModalOpen(true);
        } else {
          toast.error(response.message || '自由診療メニューの取得に失敗しました。');
        }
      } catch (err: any) {
        toast.error(
          isNotFoundError(err)
            ? 'データが見つかりません。'
            : getApiErrorMessage(err, '自由診療メニューの取得に失敗しました。')
        );
        if (isNotFoundError(err)) {
          queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
        }
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [tenant, clinicNumber, toast, queryClient]
  );

  const handleSaveTreatmentType = useCallback(
    async (data: TreatmentTypeFormData) => {
      try {
        if (isNewTreatmentType) {
          await createTreatmentTypeMutation.mutateAsync(data);
        } else if (selectedTreatmentType) {
          await updateTreatmentTypeMutation.mutateAsync({ id: selectedTreatmentType.id, data });
        }
        setTreatmentTypeEditModalOpen(false);
        setSelectedTreatmentType(null);
        setIsNewTreatmentType(false);
      } catch (err: any) {
        throw { errors: err?.errors || {} };
      }
    },
    [
      isNewTreatmentType,
      selectedTreatmentType,
      createTreatmentTypeMutation,
      updateTreatmentTypeMutation,
    ]
  );

  return {
    treatmentTypeEditModalOpen,
    setTreatmentTypeEditModalOpen,
    selectedTreatmentType,
    isNewTreatmentType,
    isLoadingDetail,
    handleNewTreatmentType,
    handleEditTreatmentType,
    deleteTreatmentTypeMutation,
    handleSaveTreatmentType,
  };
};
